import schemas from "@/utils/schemas";
import { yupResolver } from "@hookform/resolvers/yup";
import React from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Styled } from "./styles";
import { Backdrop } from "@dippi/ui";
import { Grid, Typography } from "@mui/material";
import { Forms } from "@/types/form";
import { Brand } from "@/types/entities";
import Base from "./base";
import Description from "./description";
import Socials from "./socials";
import Entities from "./entities";
import { Help } from "@dippi/icons";
import constant from "@/utils/constant";
import { useSubmit } from "./hooks";
import FileUpload from "@/components/common/file-upload";
import { useFormListeners } from "@dippi/forms";

const getDefaultValues = (brand: Brand | undefined): Forms["Brand"] => {
    const values: Forms["Brand"] = {
        email: brand?.email ?? "",
        title: brand?.title ?? "",
        tag: brand?.tag ?? "",
        media: brand?.media ?? undefined,
        websiteURL: brand?.websiteURL ?? "",
        description: brand?.description ?? "",
        socials: brand?.socials ?? [],
        entities: brand?.entities ?? [],
    };

    return values;
};

interface Props {
    brand?: Brand;
}

const Form: React.FC<Props> = props => {
    const { brand } = props;

    const methods = useForm<Forms["Brand"]>({
        resolver: yupResolver(schemas.brand),
        values: getDefaultValues(brand),
    });

    const { handleSubmit, formState } = methods;
    const { isDirty, isSubmitting } = formState;
    useFormListeners("form-brand", isDirty, isSubmitting);

    const { mutateAsync: onSubmit } = useSubmit();
    const SPACING = constant.GRID_SPACING;

    return (
        <FormProvider {...methods}>
            <Styled.Form
                id="form-brand"
                onSubmit={handleSubmit(async data => await onSubmit(data))}
            >
                <Grid container spacing={SPACING} rowGap={SPACING}>
                    <Styled.Help item md={12} display="flex" alignItems="center">
                        <Help style={{ marginRight: 5 }} />
                        <Typography
                            sx={{ opacity: 0.5, fontFamily: "Ubuntu", fontSize: "14px" }}
                            fontWeight={400}
                        >
                            Please consider that information provided on this page will be available
                            for public view
                        </Typography>
                    </Styled.Help>
                    <Styled.ImageContainer item lg={3}>
                        <FileUpload />
                    </Styled.ImageContainer>
                    <Grid item container xl={9} gap={SPACING}>
                        <Base />
                        <Description />
                        <Socials />
                        <Entities />
                    </Grid>
                </Grid>
                <Backdrop loading={isSubmitting} />
            </Styled.Form>
        </FormProvider>
    );
};

export default Form;
